import React, { useState, useEffect } from 'react';
import { PDFDocument, StandardFonts, rgb } from 'pdf-lib';
import { loadTemplate, templateExists } from '../utils/templateLoader';
import { sharePDF, formatFilename } from '../utils/pdfUtils';

const VeegbewijsForm = ({ onBack }) => {
  const [formData, setFormData] = useState({
    template: 'Houte Kachel',
    naam: '',
    adres: '',
    postcode: '',
    plaats: '',
    prijs: '',
    datum: '',
    opmerkingen: ''
  });
  const [isGenerating, setIsGenerating] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const templateNames = {
    'gashaard': 'Gashaard',
    'Houte Kachel': 'Houtkachel',
    'kolenkachel': 'Kolenkachel',
    'pelletkachel': 'Pelletkachel'
  };

  useEffect(() => {
    // Set current date
    const today = new Date();
    const formattedDate = today.getFullYear() + '-' + 
      String(today.getMonth() + 1).padStart(2, '0') + '-' + 
      String(today.getDate()).padStart(2, '0');
    setFormData(prev => ({ ...prev, datum: formattedDate }));
  }, []);

  // Prevent accidental navigation away from form with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = 'Weet je het zeker? Wil je weggaan? Alle ingevoerde gegevens gaan verloren.';
        return e.returnValue;
      }
    };

    const handlePopState = (e) => {
      if (hasUnsavedChanges) {
        const confirmLeave = window.confirm(
          'Weet je het zeker? Wil je terug gaan? Alle ingevoerde gegevens gaan verloren.'
        );
        if (!confirmLeave) {
          // Push the current state back to prevent navigation
          window.history.pushState(null, '', window.location.pathname);
        }
      }
    };

    // Add event listeners
    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('popstate', handlePopState);

    // Push initial state for popstate handling
    window.history.pushState(null, '', window.location.pathname);

    // Cleanup
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('popstate', handlePopState);
    };
  }, [hasUnsavedChanges]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Mark as having unsaved changes when user starts typing
    if (!hasUnsavedChanges) {
      setHasUnsavedChanges(true);
    }
  };



  const handleClear = () => {
    const confirmClear = window.confirm(
      'Weet je het zeker? Wil je het formulier leegmaken? Alle ingevoerde gegevens gaan verloren.'
    );
    
    if (confirmClear) {
      const today = new Date();
      const formattedDate = today.getFullYear() + '-' + 
        String(today.getMonth() + 1).padStart(2, '0') + '-' + 
        String(today.getDate()).padStart(2, '0');
      
      setFormData({
        template: 'Houte Kachel',
        naam: '',
        adres: '',
        postcode: '',
        plaats: '',
        prijs: '',
        datum: formattedDate,
        opmerkingen: ''
      });
      
      // Reset unsaved changes flag
      setHasUnsavedChanges(false);
    }
  };

  const handleBack = () => {
    if (hasUnsavedChanges) {
      const confirmLeave = window.confirm(
        'Weet je het zeker? Wil je terug gaan? Alle ingevoerde gegevens gaan verloren.'
      );
      if (confirmLeave) {
        onBack();
      }
    } else {
      onBack();
    }
  };

  const generatePDF = async () => {
    // Basic validation
    if (!formData.naam || !formData.adres || !formData.postcode || !formData.plaats || !formData.prijs) {
      alert('Vul alle vereiste velden in');
      return;
    }

    setIsGenerating(true);

    try {
      // Load the template PDF securely
      if (!templateExists(formData.template)) {
        throw new Error(`Template not found: ${formData.template}`);
      }
      
      const templateBytes = await loadTemplate(formData.template);
      
      // Load the existing PDF document
      const pdfDoc = await PDFDocument.load(templateBytes);
      
      // Get the first page
      const pages = pdfDoc.getPages();
      const firstPage = pages[0];
      
      // Embed font
      const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
      
      // Combine address
      const volledigAdres = `${formData.adres}, ${formData.postcode}`;
      
      // Add text overlays at the same coordinates as Flutter app
      // Naam (290, 233)
      firstPage.drawText(formData.naam, {
        x: 290,
        y: firstPage.getHeight() - 248 + 7,
        size: 16,
        font: font,
        color: rgb(0, 0, 0),
      });
      
      // Adres (290, 260)
      firstPage.drawText(volledigAdres, {
        x: 290,
        y: firstPage.getHeight() - 275 + 7,
        size: 16,
        font: font,
        color: rgb(0, 0, 0),
      });
      
      // Plaats (290, 288)
      firstPage.drawText(formData.plaats, {
        x: 290,
        y: firstPage.getHeight() - 304 + 8,
        size: 16,
        font: font,
        color: rgb(0, 0, 0),
      });
      
      // Prijs (380, 315)
      const formatPrice = (price) => {
        if (!price) return '€0,00';
        const numPrice = parseFloat(price.toString().replace(',', '.'));
        return new Intl.NumberFormat('nl-NL', {
          style: 'currency',
          currency: 'EUR',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }).format(numPrice);
      };
      
      firstPage.drawText(formatPrice(formData.prijs), {
        x: 380,
        y: firstPage.getHeight() - 330 + 8,
        size: 16,
        font: font,
        color: rgb(0, 0, 0),
      });
      
      // Datum (457, 459)
      if (formData.datum) {
        const dateValue = new Date(formData.datum).toLocaleDateString('nl-NL');
        firstPage.drawText(dateValue, {
          x: 457 + 4,
          y: firstPage.getHeight() - 472 + 7,
          size: 16,
          font: font,
          color: rgb(0, 0, 0),
        });
      }
      
      // Opmerkingen (360, 342) - multiple lines if needed
      if (formData.opmerkingen) {
        const lines = formData.opmerkingen.split('\n');
        for (let i = 0; i < Math.min(lines.length, 3); i++) {
          firstPage.drawText(lines[i], {
            x: 360,
            y: firstPage.getHeight() - (356 + (i * 20)) + 7,
            size: 16,
            font: font,
            color: rgb(0, 0, 0),
          });
        }
      }
      
      // Save the PDF
      const pdfBytes = await pdfDoc.save();
      
      const blob = new Blob([pdfBytes], { type: 'application/pdf' });
      
      // Format filename and share/download
      const filename = formatFilename(formData.datum, formData.naam, formData.plaats);
      await sharePDF(
        blob, 
        filename, 
        'Veegbewijs PDF', 
        `Veegbewijs voor ${formData.naam}`
      );
      
      // Reset unsaved changes flag after successful PDF generation
      setHasUnsavedChanges(false);
      
    } catch (error) {
      console.error('Error loading template:', error);
      // Fallback to simple PDF if template loading fails
      await generateSimplePDF();
    } finally {
      setIsGenerating(false);
    }
  };

  const generateSimplePDF = async () => {
    // Create a new PDF document as fallback
    const pdfDoc = await PDFDocument.create();
    const page = pdfDoc.addPage([595, 842]); // A4 size
    
    // Add fonts
    const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
    const boldFont = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
    
    // Title
    page.drawText('VEEGBEWIJS', {
      x: 50,
      y: 750,
      size: 24,
      font: boldFont,
      color: rgb(0.13, 0.59, 0.95), // Blue color
    });
    
    // Template type
    page.drawText(`Template: ${templateNames[formData.template]}`, {
      x: 50,
      y: 720,
      size: 14,
      font: boldFont,
    });
    
    // Current date
    const currentDate = new Date().toLocaleDateString('nl-NL');
    page.drawText(`Gegenereerd op: ${currentDate}`, {
      x: 400,
      y: 720,
      size: 10,
      font: font,
    });
    
    // Klantgegevens section
    let yPos = 650;
    page.drawText('KLANTGEGEVENS', {
      x: 50,
      y: yPos,
      size: 16,
      font: boldFont,
      color: rgb(0.13, 0.59, 0.95),
    });
    
    yPos -= 30;
    page.drawText(`Naam: ${formData.naam}`, { x: 50, y: yPos, size: 12, font: font });
    
    yPos -= 20;
    page.drawText(`Adres: ${formData.adres}, ${formData.postcode}`, { x: 50, y: yPos, size: 12, font: font });
    
    yPos -= 20;
    page.drawText(`Plaats: ${formData.plaats}`, { x: 50, y: yPos, size: 12, font: font });
    
    // Prijs & Datum section
    yPos -= 50;
    page.drawText('PRIJS & DATUM INFORMATIE', {
      x: 50,
      y: yPos,
      size: 16,
      font: boldFont,
      color: rgb(0.13, 0.59, 0.95),
    });
    
    yPos -= 30;
    const fallbackPriceText = formData.prijs.startsWith('€') ? `Prijs (incl. BTW): ${formData.prijs}` : `Prijs (incl. BTW): €${formData.prijs}`;
    page.drawText(fallbackPriceText, { x: 50, y: yPos, size: 12, font: font });
    
    yPos -= 20;
    const dateValue = formData.datum ? new Date(formData.datum).toLocaleDateString('nl-NL') : 'Niet opgegeven';
    page.drawText(`Datum Uitvoering: ${dateValue}`, { x: 50, y: yPos, size: 12, font: font });
    
    // Opmerkingen section
    if (formData.opmerkingen) {
      yPos -= 50;
      page.drawText('OPMERKINGEN & BEVINDINGEN', {
        x: 50,
        y: yPos,
        size: 16,
        font: boldFont,
        color: rgb(0.13, 0.59, 0.95),
      });
      
      yPos -= 30;
      const opmerkingLines = formData.opmerkingen.split('\n');
      for (const line of opmerkingLines) {
        if (yPos < 100) break; // Prevent text from going off page
        page.drawText(line, { x: 50, y: yPos, size: 12, font: font });
        yPos -= 20;
      }
    }
    
    // Footer
    page.drawText('Dit veegbewijs is digitaal gegenereerd', {
      x: 50,
      y: 50,
      size: 10,
      font: font,
      color: rgb(0.5, 0.5, 0.5),
    });
    
    // Save and download PDF
    const pdfBytes = await pdfDoc.save();
    const blob = new Blob([pdfBytes], { type: 'application/pdf' });
    
    // Format filename and share/download
    const filename = formatFilename(formData.datum, formData.naam, formData.plaats);
    await sharePDF(
      blob, 
      filename, 
      'Veegbewijs PDF', 
      `Veegbewijs voor ${formData.naam}`
    );
    
    // Reset unsaved changes flag after successful PDF generation
    setHasUnsavedChanges(false);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    generatePDF();
  };



  return (
    <>
      <div className="header">
        <span className="header-back" onClick={handleBack}>←</span>
        <span className="header-title">Veegbewijs Gegevens</span>
      </div>

      <div className="container">
        <p className="intro-text">
          Vul alle vereiste velden in om het veegbewijs te genereren
        </p>



        <form onSubmit={handleSubmit}>
          {/* Template Selectie */}
          <div className="section">
            <div className="form-group">
              <label htmlFor="template">Kies Template Type <span className="required">*</span></label>
              <select 
                id="template" 
                name="template" 
                value={formData.template}
                onChange={handleInputChange}
                required
              >
                <option value="Houte Kachel">Houtkachel</option>
                <option value="gashaard">Gashaard</option>
                <option value="kolenkachel">Kolenkachel</option>
                <option value="pelletkachel">Pelletkachel</option>
              </select>
            </div>
          </div>

          {/* Klantgegevens */}
          <div className="section">
            <div className="section-header">
              <span className="section-icon">👤</span>
              <span className="section-title">Klantgegevens</span>
            </div>
            <div className="form-group">
              <label htmlFor="naam">Naam Klant <span className="required">*</span></label>
              <input 
                type="text" 
                id="naam" 
                name="naam" 
                value={formData.naam}
                onChange={handleInputChange}
                placeholder="Voer naam van klant in" 
                required 
              />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="adres">Adres <span className="required">*</span></label>
                <input 
                  type="text" 
                  id="adres" 
                  name="adres" 
                  value={formData.adres}
                  onChange={handleInputChange}
                  placeholder="Voer adres in" 
                  required 
                />
              </div>
              <div className="form-group">
                <label htmlFor="postcode">Postcode <span className="required">*</span></label>
                <input 
                  type="text" 
                  id="postcode" 
                  name="postcode" 
                  value={formData.postcode}
                  onChange={handleInputChange}
                  placeholder="Voer postcode in" 
                  required 
                />
              </div>
            </div>
            <div className="form-group">
              <label htmlFor="plaats">Plaats <span className="required">*</span></label>
              <input 
                type="text" 
                id="plaats" 
                name="plaats" 
                value={formData.plaats}
                onChange={handleInputChange}
                placeholder="Voer plaats in" 
                required 
              />
            </div>
          </div>

          {/* Prijs & Datum */}
          <div className="section">
            <div className="section-header">
              <span className="section-icon">€</span>
              <span className="section-title">Prijs & Datum Informatie</span>
            </div>
            <div className="form-group">
              <label htmlFor="prijs">Prijs (incl. BTW) <span className="required">*</span></label>
              <input 
                type="text" 
                inputMode="decimal"
                id="prijs" 
                name="prijs" 
                value={formData.prijs}
                onChange={handleInputChange}
                placeholder="Bedrag in euro's" 
                required 
              />
            </div>
            <div className="form-group">
              <label htmlFor="datum">Datum Uitvoering</label>
              <input 
                type="date" 
                id="datum" 
                name="datum" 
                value={formData.datum}
                onChange={handleInputChange}
              />
            </div>
          </div>

          {/* Opmerkingen */}
          <div className="section">
            <div className="form-group">
              <label htmlFor="opmerkingen">Opmerkingen & Bevindingen</label>
              <textarea 
                id="opmerkingen" 
                name="opmerkingen" 
                value={formData.opmerkingen}
                onChange={handleInputChange}
                placeholder="Voer eventuele opmerkingen in"
              />
            </div>
          </div>

          {/* Buttons */}
          <div className="button-group">
            <button type="submit" className="btn btn-primary" disabled={isGenerating}>
              <span>{isGenerating ? 'Genereren...' : 'Genereer & Deel PDF'}</span>
            </button>
            <button type="button" className="btn btn-secondary" onClick={handleClear}>
              <span>Formulier Leegmaken</span>
            </button>
          </div>
        </form>
      </div>
    </>
  );
};

export default VeegbewijsForm;